import * as Yup from 'yup';
import { endpoints } from 'src/utils/axios';

export const blockConfig = {
    // Entity metadata
    entity: {
        name: 'Block',
        namePlural: 'Block',
        apiEndpoint: endpoints.blockMaster,
        storageKey: 'block-master-list-settings',
        rights: { module: 'master', menu: 'jurisdiction', submenu: 'block' },
    },

    // Data fetching configuration (optimized)
    fetch: {
        listFields: ['block_name', 'city', 'state', 'country', 'status', 'createdAt', 'updatedAt'],
        listPopulate: ['city', 'state', 'country'],
    },

    // API endpoints
    endpoints: {
        list: endpoints.blockMaster.list,
        create: endpoints.blockMaster.create,
        update: (id) => endpoints.blockMaster.update(id),
        delete: (id) => endpoints.blockMaster.delete(id),
        getById: (id) => endpoints.blockMaster.details(id),
    },

    // Form configuration
    form: {
        title: {
            create: 'Create New Block',
            edit: 'Edit Block',
        },
        fields: [
            {
                name: 'state',
                label: 'State',
                type: 'autocomplete',
                apiEndpoint: `${endpoints.stateMaster.list}?status=active&limit=0`,
                optionLabel: 'state_name',
                optionValue: '_id',
                placeholder: 'Select state',
                validation: Yup.mixed().required('State is required'),
                gridProps: { xs: 12, sm: 6 },
            },
            {
                name: 'city',
                label: 'City',
                type: 'autocomplete',
                apiEndpoint: (formData) => {
                    const { state } = formData || {};
                    if (!state) return null;
                    const stateId = state._id || state.id || state;
                    return `${endpoints.cityMaster.list}?status=active&limit=0&state=${stateId}`;
                },
                optionLabel: 'city_name',
                optionValue: '_id',
                placeholder: 'Select city',
                validation: Yup.mixed().required('City is required'),
                gridProps: { xs: 12, sm: 6 },
                dependsOn: 'state',
            },
            {
                name: 'block_name',
                label: 'Block Name',
                type: 'text',
                placeholder: 'Enter block name',
                validation: Yup.string()
                    .required('Block name is required')
                    .min(2, 'Block name must be at least 2 characters'),
                gridProps: { xs: 12 },
            },
            {
                name: 'status',
                label: 'Status',
                type: 'select',
                defaultValue: 'active',
                validation: Yup.string().required('Status is required'),
                options: [
                    { value: 'active', label: 'Active' },
                    { value: 'inactive', label: 'Inactive' },
                ],
                gridProps: { xs: 12 },
            },
        ],
        showAvatar: false,
        maxWidth: 'sm',
    },

    // Transform payload before submit
    transformPayload: (formData) => {
        const stateValue = formData.state?.id || formData.state?._id || formData.state;
        const cityValue = formData.city?.id || formData.city?._id || formData.city;
        return {
            ...formData,
            state: stateValue,
            city: cityValue,
        };
    },

    // Normalize data for form (Pre-fill objects for Autocomplete)
    normalizeDataForForm: (item) => {
        if (!item) return item;
        return {
            ...item,
            state: item.state,
            city: item.city,
        };
    },

    // Table configuration
    table: {
        columns: [
            {
                id: 'block_name',
                label: 'Block Name',
                width: 250,
                sortable: true,
                searchable: true,
                type: 'text',
            },
            {
                id: 'country',
                label: 'Country',
                width: 200,
                sortable: true,
                type: 'text',
                render: (value) => value?.country_name || '-',
            },
            {
                id: 'city',
                label: 'City',
                width: 200,
                sortable: true,
                type: 'text',
                render: (value) => value?.city_name || '-',
            },
            {
                id: 'state',
                label: 'State',
                width: 200,
                sortable: true,
                type: 'text',
                render: (value) => value?.state_name || '-',
            },
            {
                id: 'status',
                label: 'Status',
                width: 120,
                type: 'status',
                sortable: true,
                align: 'center',
                render: (value) => ({
                    label: value === 'active' ? 'Active' : 'Inactive',
                    color: value === 'active' ? 'success' : 'error',
                }),
            },
            {
                id: 'createdAt',
                label: 'Created At',
                width: 150,
                type: 'date',
                sortable: true,
                format: 'dd MMM yyyy',
            },
        ],
        defaultSort: { field: 'createdAt', direction: 'desc' },
        rowsPerPageOptions: [5, 10, 25, 50],
        defaultRowsPerPage: 10,
    },

    // Filter configuration
    filters: {
        default: {
            search: '',
            status: 'all',
            state: '',
            city: '',
        },
        fields: [
            {
                name: 'search',
                label: 'Search',
                type: 'search',
                placeholder: 'Search blocks...',
                apiParam: 'search',
            },
            {
                name: 'status',
                label: 'Status',
                type: 'tabs',
                options: [
                    { value: 'all', label: 'All', color: 'default' },
                    { value: 'active', label: 'Active', color: 'success' },
                    { value: 'inactive', label: 'Inactive', color: 'error' },
                ],
                apiParam: 'status',
                showCount: true,
            },
            {
                name: 'state',
                label: 'State',
                type: 'autocomplete',
                apiEndpoint: `${endpoints.stateMaster.list}?status=active&limit=0`,
                optionLabel: 'state_name',
                optionValue: '_id',
                optionLabelSecondary: 'country.country_name',
                apiParam: 'state',
                placeholder: 'Filter by state',
                inAdvancedFilters: true,
            },
            {
                name: 'city',
                label: 'City',
                type: 'autocomplete',
                apiParam: 'city',
                apiEndpoint: (formData) => {
                    const { state } = formData || {};
                    if (!state || state === 'all') return `${endpoints.cityMaster.list}?status=active&limit=0`;
                    const stateId = state._id || state.id || state;
                    return `${endpoints.cityMaster.list}?status=active&limit=0&state=${stateId}`;
                },
                optionLabel: 'city_name',
                optionValue: '_id',
                dependsOn: 'state',
                placeholder: 'Filter by city',
                inAdvancedFilters: true,
            },
        ],
    },

    // Card view configuration
    card: {
        title: (item) => item.block_name,
        subtitle: (item) => `${item.city?.city_name}, ${item.state?.state_name}`,
        avatar: false,
        status: (item) => ({
            label: item.status === 'active' ? 'Active' : 'Inactive',
            color: item.status === 'active' ? 'success' : 'error',
        }),
        metadata: [
            {
                label: 'City',
                value: (item) => item.city?.city_name || '-',
                icon: 'solar:city-bold-duotone',
            },
            {
                label: 'State',
                value: (item) => item.state?.state_name || '-',
                icon: 'solar:map-point-bold-duotone',
            },
        ],
    },

    // Permissions & features
    features: {
        create: true,
        edit: true,
        delete: true,
        bulkDelete: true,
        duplicate: true,
        export: false,
        import: false,
        viewToggle: true,
        columnVisibility: true,
    },
};
